import { RAW_cssValue, ThemeUIStyleObject } from "@classdojo/web/nessie/stylingLib";
import { TranslatedString } from "@classdojo/web/pods/i18n/translate";
import { Component } from "react";
import { connect } from "react-redux";
import { compose, withState, withHandlers, lifecycle } from "recompose";
import PhoneWatchVideo from "./PhoneWatchVideo";
import WatchVideo from "./WatchVideo";
import {
  appMetadata,
  RECEIVE_IDEAS,
  selectIsPlaying,
  selectSeekOffset,
  sendHideVideo,
  sendVideoIsFullscreen,
  sendVideoPlaybackState,
  sendSeekOffset,
  IdeasSlice,
} from "src/pods/ideas";
import { Lesson, Slide } from "src/pods/ideas/data/types";
import { selectPaired, selectDisplayOn, RealtimeState } from "src/pods/realtime";
import AppModal from "src/shared/components/AppModal";
import logEvent from "src/utils/logEvent";

type WatchVideoContainerProps = {
  // eslint-disable-next-line react/no-unused-prop-types
  displayOpen: VoidFunction;
  displayClose: VoidFunction;
  lesson: Lesson;
  watch: Slide;
  onBack: VoidFunction;
};
type WatchVideoInnerProps = WatchVideoContainerProps & {
  isCasting?: boolean;
  isConnected?: boolean;
  isFullscreen?: boolean;
  isPlaying?: boolean;

  shouldPlayOnPhone?: boolean;

  onVideoReplay: VoidFunction;
  onVideoForward: VoidFunction;
  onTogglePlay: VoidFunction;
  onPlayOnPhone: VoidFunction;
  onToggleFullscreen: VoidFunction;
  onPlayOnComputer: VoidFunction;
  onRequestStart: VoidFunction;
};

// eslint-disable-next-line react-prefer-function-component/react-prefer-function-component
class WatchVideoContainer extends Component<WatchVideoInnerProps> {
  render() {
    const {
      isCasting,
      isConnected,
      isPlaying,
      isFullscreen,
      watch,
      lesson,
      onBack,
      displayClose,
      onRequestStart,
      shouldPlayOnPhone,
      onPlayOnPhone,
      onPlayOnComputer,
      onToggleFullscreen,
      onTogglePlay,
      onVideoForward,
      onVideoReplay,
    } = this.props;

    return (
      <article sx={appStyle}>
        <AppModal
          title={lesson.title as TranslatedString}
          onRequestStart={onRequestStart}
          onRequestStop={onBack}
          backgroundColor={appMetadata().backgroundColor}
          onRequestHide={() => {
            displayClose();
            onBack();
          }}
          sx={{ marginTop: "dt_xxl" }}
        >
          {(shouldPlayOnPhone || !isCasting || !isConnected) && (
            <PhoneWatchVideo watch={watch} canPlayOnComputer={isConnected} onPlayOnComputer={onPlayOnComputer} />
          )}
          {!shouldPlayOnPhone && isCasting && isConnected && (
            <WatchVideo
              watch={watch}
              lesson={lesson}
              isPlaying={isPlaying}
              isFullscreen={isFullscreen}
              onPlayOnPhone={onPlayOnPhone}
              onToggleFullscreen={onToggleFullscreen}
              onTogglePlay={onTogglePlay}
              onForward={onVideoForward}
              onReplay={onVideoReplay}
            />
          )}
        </AppModal>
      </article>
    );
  }
}

const mapStateToProps = (state: IdeasSlice & RealtimeState) => {
  return {
    isConnected: selectPaired(state),
    isCasting: selectDisplayOn(state),
    isPlaying: selectIsPlaying(state),
    seekOffset: selectSeekOffset(state),
  };
};

const mapDispatchToProps = {
  sendHideVideo,
  sendSeekOffset,
  sendVideoPlaybackState,
  sendVideoIsFullscreen,
};

export default compose<WatchVideoInnerProps, WatchVideoContainerProps>(
  connect(mapStateToProps, mapDispatchToProps),
  withState("shouldPlayOnPhone", "setShouldPlayOnPhone", false),
  withHandlers({
    displayOpenWithVideo:
      ({ displayOpen, watch }) =>
      () => {
        displayOpen("/apps/display/ideas", {
          type: RECEIVE_IDEAS,
          backgroundOverlay: true,
          payload: {
            question: null,
            watch,
            isPlaying: false,
          },
        });
      },
  }),
  withHandlers({
    onPlayOnPhone:
      ({ setShouldPlayOnPhone, displayClose }) =>
      () => {
        setShouldPlayOnPhone(true);
        displayClose();
      },
    onPlayOnComputer:
      ({ setShouldPlayOnPhone, displayOpenWithVideo }) =>
      () => {
        setShouldPlayOnPhone(false);
        displayOpenWithVideo();
      },
    onTogglePlay:
      ({ sendVideoPlaybackState, isPlaying }) =>
      () =>
        sendVideoPlaybackState(!isPlaying),
    onVideoForward:
      ({ sendSeekOffset, seekOffset }) =>
      (timeInSeconds: number) =>
        sendSeekOffset(seekOffset + timeInSeconds),
    onVideoReplay:
      ({ sendSeekOffset, seekOffset }) =>
      (timeInSeconds: number) =>
        sendSeekOffset(seekOffset - timeInSeconds),
    onToggleFullscreen:
      ({ sendVideoIsFullscreen, isFullscreen }) =>
      () =>
        sendVideoIsFullscreen(!isFullscreen),
    onRequestStart:
      ({ displayOpenWithVideo, setShouldPlayOnPhone }) =>
      () => {
        setShouldPlayOnPhone(false);
        displayOpenWithVideo();
      },
  }),
  lifecycle<
    {
      displayOpenWithVideo: VoidFunction;
      displayClose: VoidFunction;
      sendVideoPlaybackState: (value: boolean) => void;
    },
    unknown
  >({
    componentDidMount: function componentDidMount() {
      logEvent({
        eventName: "ideas.setup.open",
      });

      const { displayOpenWithVideo } = this.props;
      displayOpenWithVideo();
    },
    componentWillUnmount: function componentWillUnmount() {
      logEvent({
        eventName: "ideas.setup.close",
      });

      const { displayClose, sendVideoPlaybackState } = this.props;

      sendVideoPlaybackState(false);
      displayClose();
    },
  }),
)(WatchVideoContainer);

const appStyle: ThemeUIStyleObject = {
  textAlign: "center",
  padding: RAW_cssValue("20px"),
};
